import { useMemo } from "react";
import { useWorkflowFields } from "@repo/shared-components";
import type { SelectOption } from "@repo/ui";
import { useBrandsQuery } from "~/apis/use-brands-query";
import { useFoldersQuery } from "~/apis/use-folders-query";
import { useWorkflowsQuery } from "~/apis/use-workflows-query";
import type { Brand, Workflow, Folder } from "~/types/api";
import type { TaskManagerDTO } from "~/types/api-custom";

export const useTaskData = (
  task?: TaskManagerDTO,
  selectedBrandId?: number | null,
  selectedWorkflowId?: number
) => {
  const isEditMode = !!task;

  const { data: brands } = useBrandsQuery();
  const { data: workflows } = useWorkflowsQuery();

  const { data: folders } = useFoldersQuery(selectedBrandId || 0, {
    enabled: !!selectedBrandId,
  });

  const { fields: workflowFields, isLoading: isLoadingFields } = useWorkflowFields(
    selectedWorkflowId,
    !!selectedWorkflowId
  );

  const originalFolderId = useMemo(() => {
    if (!isEditMode || !folders || !task?.id) return "";
    const folderWithTask = folders.find((f: Folder) =>
      f.folderTasks?.some((ft) => ft.taskId === task.id)
    );
    return folderWithTask ? String(folderWithTask.id) : "";
  }, [folders, task?.id, isEditMode]);

  const brandOptions = useMemo<SelectOption[]>(
    () =>
      brands?.map((brand: Brand) => ({
        label: brand.name,
        value: String(brand.id),
      })) || [],
    [brands]
  );

  const selectedBrandCustomerId = useMemo(() => {
    if (!selectedBrandId || !brands) return null;
    const brand = brands.find((b: Brand) => b.id === selectedBrandId);
    return brand?.customerId ?? null;
  }, [brands, selectedBrandId]);

  const workflowOptions = useMemo<SelectOption[]>(
    () =>
      workflows
        ?.filter((workflow: Workflow) => {
          const matchesCustomer =
            !selectedBrandCustomerId || workflow.customerId === selectedBrandCustomerId;
          const isActive = workflow.state === "Active";
          const isSelected = workflow.id === selectedWorkflowId;
          return matchesCustomer && (isActive || (isEditMode && isSelected));
        })
        .map((workflow: Workflow) => ({
          label: workflow.name,
          value: String(workflow.id),
        })) || [],
    [workflows, selectedBrandCustomerId]
  );

  const folderOptions = useMemo<SelectOption[]>(
    () =>
      folders?.map((folder: Folder) => ({
        label: folder.name,
        value: String(folder.id),
      })) || [],
    [folders]
  );

  return {
    brands,
    workflows,
    folders,
    workflowFields,
    isLoadingFields,
    originalFolderId,
    brandOptions,
    workflowOptions,
    folderOptions,
  };
};
